import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import useCrmApi from "../../../components/hook/crm-req";
import useRequest from "../../../components/hook/use-req";
import { DealBoardItem } from "./types";

// Edit an existing deal — PATCH /deals/{id}, same field set the "existing
// deal CRUD endpoints" already use elsewhere (customers/details/deals.tsx).

interface TeamMember {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

const inputClass =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20";

const toDateTimeLocal = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 16);
};

const EditDealModal: React.FC<{ isOpen: boolean; onClose: () => void; deal: DealBoardItem; onSaved: (updated: Partial<DealBoardItem>) => void }> = ({
  isOpen,
  onClose,
  deal,
  onSaved,
}) => {
  const token = localStorage.getItem("authToken");
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState(deal.title || "");
  const [productOrService, setProductOrService] = useState(deal.product_or_service || "");
  const [amount, setAmount] = useState(deal.amount != null ? String(deal.amount) : "");
  const [currency, setCurrency] = useState(deal.currency || "INR");
  const [assignedTo, setAssignedTo] = useState(deal.assigned_to_user_id || "");
  const [description, setDescription] = useState(deal.description || "");
  const [expectedClose, setExpectedClose] = useState(toDateTimeLocal(deal.expected_close_at));

  const { makeRequest: updateDeal } = useCrmApi("/deals", "PATCH", { Authorization: `Bearer ${token}` });
  const { makeRequest: getTeamList } = useRequest("/profile/users", "GET", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    if (!isOpen) return;
    setTitle(deal.title || "");
    setProductOrService(deal.product_or_service || "");
    setAmount(deal.amount != null ? String(deal.amount) : "");
    setCurrency(deal.currency || "INR");
    setAssignedTo(deal.assigned_to_user_id || "");
    setDescription(deal.description || "");
    setExpectedClose(toDateTimeLocal(deal.expected_close_at));
    (async () => {
      try {
        const [response] = await getTeamList();
        if (Array.isArray(response)) setTeam(response);
        else if (Array.isArray(response?.users)) setTeam(response.users);
        else if (Array.isArray(response?.results)) setTeam(response.results);
      } catch {
        setTeam([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, deal.id]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Give this deal a title.");
      return;
    }
    setSubmitting(true);
    const payload = {
      title: title.trim(),
      product_or_service: productOrService.trim() || null,
      amount: amount.trim() === "" ? null : amount.trim(),
      currency: currency || null,
      assigned_to_user_id: assignedTo || null,
      description: description.trim() || null,
      expected_close_at: expectedClose ? new Date(expectedClose).toISOString() : null,
    };
    const [response, status] = await updateDeal(payload, undefined, deal.id);
    setSubmitting(false);

    if (status >= 200 && status < 300) {
      toast.success("Deal updated.");
      onSaved(payload);
      onClose();
    } else {
      toast.error(response?.detail || response?.message || "Failed to update deal.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Edit Deal</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Product / Service</label>
            <input value={productOrService} onChange={(e) => setProductOrService(e.target.value)} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Amount</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Currency</label>
              <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Assigned To</label>
            <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className={inputClass}>
              <option value="">Unassigned</option>
              {team.map((m) => (
                <option key={m.id} value={m.id}>
                  {`${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email || m.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Expected Close</label>
            <input type="datetime-local" value={expectedClose} onChange={(e) => setExpectedClose(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${inputClass} resize-none`} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 px-5 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg shadow-sm"
          >
            {submitting ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDealModal;
